import React, { useEffect, useMemo, useState } from "react";
import { IPaymentFields, getApiCallState, useStateSelector } from "../Models";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { BookingSummary } from "./BookingSummary";
import { PassengerDetails } from "./PassengerDetails";
import { FormProvider, useForm } from "react-hook-form";
import { BookingFooter } from "../Components/BookingFooter";
import { useDispatch } from "react-redux";
import { abortPayment, cancelBooking, clearDiscountCodes, clearTemporaryTable, finalizeBooking, setStep } from "../Actions";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { clearApiCallState } from "../ApiCalls/Actions";
import { FakePaymentDialog } from "../Payment/FakePayment";
import { getSettings } from "../Settings";
import { Payment } from "./Payment";
import { AdyenDialog } from "../Payment/Adyen";
import SvgBoxArrowUpRight from "../../Shared/Icons/bootstrap/BoxArrowUpRight";
import SvgAlertCircle from "../../Shared/Icons/bootstrap/AlertCircle";
import { ExpirationCounter } from "./ExpirationCounter";
import SvgGift from "../../Shared/Icons/bootstrap/Gift";
import { IErrorDictionary } from "../ApiCalls/ApiCallReducer";
import { trackBeginCheckout } from "../../Shared/Tracking";
import { Vouchers } from "./Vouchers";
import dayjs from "dayjs";
import { RedirectModal } from "../Components/RedirectModal";
import { DiscountCodes } from "./DiscountCodes";
import { getErrorAsStrings } from "../Shared/Utils";

const strings = getStrings().component.orderDetails;
const timeoutStrings = getStrings().component.orderTimeout;

export interface IPassengerAndPaymentFields extends IPaymentFields
{
	passengers: IPassengerFields[];
	companyName?: string;
	companyRegistrationNo?: string;
};

export interface IPassengerFields
{
	id: string;
	type: string;
	firstname?: string;
	lastname?: string;
	phoneNumber?: string;
	phoneCountry?: string;
	email?: string;
	birthDate?: string;
	countryOfResidence?: string;
	vasaid?: string;
};

export const PassengersAndPayment = () => {

	const dispatch = useDispatch();

	// const [showBookingJson, setShowBookingJson] = useState(false);
	const [highlightTerms, setHighlightTerms] = useState(false);
	const [showVouchers, setShowVouchers] = useState(false);
	const [termsChecked, setTermsChecked] = useState(false);

	const formMethods = useForm<IPassengerAndPaymentFields>({ mode: "onBlur" });
	const isValid = termsChecked && (!formMethods.formState.errors || Object.keys(formMethods.formState.errors).length == 0) //Det verkar inte som isValid är pålitligt i detta formulär, därför kollar vi istället att det inte finns några errors
	const customerEmail = formMethods.watch("passengers.0.email");

	const state = useStateSelector(state => state.passengersAndPayment);
	const tableBookings = useStateSelector(state => state.travelDetails.tableBookings?.bookings);

	const apicalls = getApiCallState();
	const finalizeState = apicalls.getLoadingState("finalizeBooking");
	const finalizeErrors = finalizeState === "error" ? apicalls.getError("finalizeBooking") : null;

	const paymentMethod = formMethods.watch("paymentMethod");
	const paymentCountry = formMethods.watch("passengers.0.phoneCountry");
	// const requireCompany = paymentMethod === "adyen.klarna_b2b";

	const totalPrice = useMemo(() => {
		const bookingPrice = state.booking?.totalPrice || 0;

		const discountCodePercent = state.discountCodes?.[0]?.percent || 0;
		const discountCodeAmount = ((bookingPrice * discountCodePercent / 100) + state.discountCodes?.[0]?.amount)|| 0;
		const voucherAmount = state.vouchers?.reduce((acc, curr) => acc + curr.amount, 0) || 0;

		return Math.max(bookingPrice - discountCodeAmount - voucherAmount, 0);
	}, [state.booking, state.vouchers, state.discountCodes]);

	const travelDates = useMemo(() => {
		const dates = [] as string[];
		if (state.booking?.outbound?.departure)
			dates.push(dayjs(state.booking.outbound.departure).format("YYYY-MM-DD"));
		if (state.booking?.inbound?.departure)
			dates.push(dayjs(state.booking.inbound.departure).format("YYYY-MM-DD"));
		return dates;
	}, [state.booking]);

	const showVasaId = useMemo(() => {
		return state.booking?.outbound?.bookingTariffSegments.some(x => x.arrivalStation.uicStationCode === "740074531");
	}, [state.booking]);

	const settings = getSettings();
	const termsUrl = settings.termsUrl;
	const allowFakePayments = settings.allowFakePayments;
	const voucherError = apicalls.getError("verifyVoucher");
	const discountCodeError = apicalls.getError("verifyDiscountCode");

	const clearFinalizeState = () => dispatch(clearApiCallState("finalizeBooking"));

	useEffect(() => {
		if (paymentMethod && !termsChecked)
			setTimeout(() => setHighlightTerms(true), 7000);
		else
			setHighlightTerms(false);
	}, [paymentMethod, termsChecked]);

	useEffect(() => {
		if (totalPrice === 0)
			formMethods.setValue("paymentMethod", "none");
	}, [totalPrice]);

	//Tracking
	useEffect(() => {
		if (!state.booking?.bookingNumber || !state.booking.outbound)
			return;

		trackBeginCheckout(
			state.booking.outbound.departureStation.name,
			state.booking.outbound.arrivalStation.name,
			state.booking.totalPrice,
			state.booking.inbound ? true : false);
	}, [state.booking?.bookingNumber]);

	const bookingExpired = () => {
		if (state.booking)
			dispatch(cancelBooking(state.booking.bookingNumber));

		//Avboka alla temporära bordsbokningar!
		tableBookings?.forEach(x => dispatch(clearTemporaryTable(x.bookingId)));

		alert(timeoutStrings.expired);

		dispatch(setStep("search"));
	};

	const back = () => {
		dispatch(cancelBooking(state.booking.bookingNumber));
		dispatch(clearDiscountCodes());

		const step = state.booking.inbound ? "inbound" : "outbound";
		dispatch(setStep(step));
	};

	const termsCheckedClicked = (checked: boolean) => {
		setTermsChecked(checked);
	};

	const submit = (data: IPassengerAndPaymentFields) => {
		// console.debug("submit", data);

		if (finalizeState === "loading" || !termsChecked)
			return;

		const payload = {
			paymentMethod: data.paymentMethod,
			company: data.companyName ? {
				name: data.companyName,
				regNo: data.companyRegistrationNo
			} : null,
			passengers: data.passengers,
			vouchers: state.vouchers,
			discountCodes: state.discountCodes,
			tableBookings
		}

		dispatch(finalizeBooking(state.booking.bookingNumber, payload));
	};

	return <>
		<ExpirationCounter expiryTimestamp={state.booking?.expiryTimestamp} onExpiration={bookingExpired} />

		<FormProvider {...formMethods}>
			<Form onSubmit={formMethods.handleSubmit(submit)}>
				<div className="body">
					<div className="content order-details">
						<Row>
							<Col xs={{ span: 12, order: "last" }} lg={{ span: 6, order: "first" }} xl={{ order: "first" }}>
								<section className="travellers">
									<h2>{strings.travellersHeading}</h2>
									{ state.booking.passengers.map((p, idx) =>
										<PassengerDetails key={p.id} index={idx} passenger={p} requireBirthDateAndCountry={state.booking.requireBirthDateAndCountry} showVasaId={showVasaId} />)}
								</section>

								<DiscountCodes bookingNumber={state.booking.bookingNumber} email={customerEmail} vouchers={state.discountCodes} error={discountCodeError} remainingAmount={totalPrice} travelDates={travelDates} />

								{ showVouchers && <Vouchers bookingNumber={state.booking.bookingNumber} email={customerEmail} vouchers={state.vouchers} error={voucherError} remainingAmount={totalPrice} travelDates={travelDates} /> }

								{ totalPrice > 0 &&
									<section className="payment">
										<h2>{strings.paymentTypeHeading}</h2>

										{ !showVouchers && <div>
											<SvgGift /> <Button variant="link" onClick={() => setShowVouchers(true)}>{strings.vouchers.showButtonText}</Button>
										</div> }

										<Payment settings={getSettings()} paymentCountry={paymentCountry} />

									</section>}

									{/* { requireCompany && <section className="company">
										<h2>Företagsuppgifter</h2>
										<Alert variant="info">Betalsättet du valt kräver företagsuppgifter.</Alert>
										<Form.Row>
											<Form.Group as={Col} xs={12} sm={6} controlId="companyName">
												<Form.Label>Företag</Form.Label>
												<Form.Control type="text" autoComplete="company" placeholder="Ange företagsnamn"
													{...formMethods.register("companyName", { required: true })} className={validationCssClass(formMethods.formState, "companyName")} />
											</Form.Group>
											<Form.Group as={Col} xs={12} sm={6} controlId="companyRegistrationNo">
												<Form.Label>Organisationsnummer</Form.Label>
												<Form.Control type="text" autoComplete="orgnr" placeholder="Ange orgnr, 10 siffror"
													{...formMethods.register("companyRegistrationNo", { required: true }) } className={validationCssClass(formMethods.formState, "companyRegistrationNo")} />
											</Form.Group>
										</Form.Row>
									</section>} */}
								{ totalPrice === 0 && state.vouchers?.length > 0 && <Alert variant="info"><span dangerouslySetInnerHTML={ { __html: strings.vouchers.remainingAmountInfo } }></span></Alert> }

								<div className="mt-3">
									{ highlightTerms && !termsChecked ? <div className="alert alert-warning"><SvgAlertCircle /> {strings.termsReminder}</div> : null}

									<Form.Check custom type="checkbox" label={strings.termsLabel} id="termsChecked" className="mt-2" onClick={e => termsCheckedClicked((e.target as HTMLInputElement).checked)} checked={termsChecked} />
									{ termsUrl ? <a href={termsUrl} className="small" target="_blank">{strings.termsLinkText} <SvgBoxArrowUpRight /></a> : null}
								</div>
							</Col>
							<Col xs={{ span: 12, order: "first" }} lg={{ span: 6, order: "last" }} xl={{ order: "last" }}>
								<section className="order-summary-section">
									<h2>{strings.orderSummaryHeading}</h2>

									<BookingSummary booking={state.booking} />
								</section>
							</Col>
						</Row>

						{/* { state && <>
							<Button variant="link" onClick={() => setShowBookingJson(!showBookingJson)}><SvgEye /> {showBookingJson ? "Dölj" : "Visa"} json för bokning</Button>
							{ showBookingJson && <pre><code>{JSON.stringify(state.booking, null, 2)}</code></pre> }
						</> } */}
					</div>

					<BookingFooter backText={strings.backButtonText} onBack={back} price={totalPrice}
						submitButton={<Button variant="primary" type="submit" disabled={!isValid || finalizeState === "loading"}>{strings.submitButtonText}</Button>}
					/>
				</div>
			</Form>
		</FormProvider>

		<Modal show={finalizeState === "loading"} backdrop="static" keyboard={false} centered>
			<Modal.Body>
				<div className="mt-3 mb-3">{strings.finalizingOrder} <SpinnerSmall /></div>
			</Modal.Body>
		</Modal>
		<Modal show={finalizeState === "error"} backdrop="static" centered onHide={clearFinalizeState}>
			<Modal.Body>
				<strong>{strings.finalizingOrderFailed}</strong><br />
				{renderErrors(finalizeErrors)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={clearFinalizeState}>OK</Button>
			</Modal.Footer>
		</Modal>

		{ allowFakePayments && state.paymentMethod === "fake" && state.booking &&
			<FakePaymentDialog show={state.booking.isFinalized === true} bookingNumber={state.booking.bookingNumber}
				amount={state.paymentData?.amount} isPaid={state.booking.isPaid} onCancel={() => dispatch(abortPayment())} /> }

		{ state.paymentData?.method === "adyen" &&
			<AdyenDialog amount={state.booking.totalPrice} clientKey={state.paymentData.adyenData.checkoutKey} environment={state.paymentData.adyenData.postData.environment}
				paymentId={state.paymentData.adyenData.sessionId} sessionData={state.paymentData.adyenData.postData.sessionData} bookingNumber={state.booking.bookingNumber}
				onPaymentAborted={() => dispatch(abortPayment())}/>}

		<RedirectModal show={state.paymentData?.method === "none" && state.paymentData?.amount === 0}
			redirectUrl={`${settings.receiptUrl}?bookingNumber=${state.booking.bookingNumber}&paymentReference=${state.vouchers?.length > 0 ? state.vouchers[0].voucherCode : ""}`}
			message={<><h4>Bokningen är betald</h4>Vi har tagit emot din betalning, vänta eller tryck på OK så skickas du vidare till kvittosidan.</>}
			redirectAfterSeconds={10}/>
	</>;
};

export const renderErrors = (errors?: string|IErrorDictionary) => {
	const errorMessages = getErrorAsStrings(errors);


	if (!errorMessages || errorMessages.length === 0)
		return null;

	if (errorMessages.length === 1)
		return <div className="mt-2">{errorMessages[0]}</div>;

	return <ul className="mt-2">{errorMessages.map((x, idx) => <li key={idx}>{x}</li> )}</ul>
};

